import type { AppMode } from '@/types/appMode';
import type { LucideIcon } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { NavGates } from '@/config/navVisibility';
import {
  LayoutDashboard,
  FolderOpen,
  BarChart3,
  Settings,
  Clock,
  Rocket,
  Banknote,
  Library,
  Mic,
  Leaf,
} from "lucide-react";

const allModes: AppMode[] = ['accounting', 'projects', 'sustainability'];

/** Gates (`requiresScope`, `featureFlag`, …) are evaluated by the shared `isNavItemVisible`. */
export interface NavigationItem extends NavGates {
  name: string;
  href: string;
  icon: LucideIcon;
  modes: AppMode[];
}

// Factory function for navigation items with i18n support
export const getNavigationItems = (t: TFunction): NavigationItem[] => [
  { name: t("nav.primary.dashboard"), href: "dashboard", icon: LayoutDashboard, modes: allModes },
  { name: t("nav.primary.accounting"), href: "accounting/overview", icon: Banknote, modes: allModes, requiresScope: 'accounting' },
  { name: t("nav.primary.projects"), href: "projects", icon: FolderOpen, modes: allModes },
  { name: t("nav.primary.impact"), href: "impact", icon: Leaf, modes: allModes },
  { name: t("nav.primary.resources"), href: "resources/assets", icon: Library, modes: allModes },
  { name: t("nav.primary.settings"), href: "settings", icon: Settings, modes: allModes },
];

// Factory function for tools items with i18n support
export const getToolsItems = (t: TFunction): NavigationItem[] => [
  { name: t("nav.tools.timesheets"), href: "timesheets", icon: Clock, modes: allModes },
  { name: t("nav.tools.memos"), href: "memos", icon: Mic, modes: allModes, featureFlag: 'memos' },
  { name: t("nav.tools.reports"), href: "reports", icon: BarChart3, modes: allModes },
  { name: t("nav.tools.levelUp"), href: "level-up", icon: Rocket, modes: allModes, featureFlag: 'level_up' },
];

// Static exports for backward compatibility (used by AppModeContext)
// These use a simple identity function since they're only used for route matching
export const navigationItems: NavigationItem[] = [
  { name: "Dashboard", href: "dashboard", icon: LayoutDashboard, modes: allModes },
  { name: "Accounting", href: "accounting/overview", icon: Banknote, modes: allModes, requiresScope: 'accounting' },
  { name: "Projects", href: "projects", icon: FolderOpen, modes: allModes },
  { name: "Impact", href: "impact", icon: Leaf, modes: allModes },
  { name: "Resources", href: "resources/assets", icon: Library, modes: allModes },
  { name: "Settings", href: "settings", icon: Settings, modes: allModes },
];

export const toolsItems: NavigationItem[] = [
  { name: "Timesheets", href: "timesheets", icon: Clock, modes: allModes },
  { name: "Memos", href: "memos", icon: Mic, modes: allModes, featureFlag: 'memos' },
  { name: "Reports", href: "reports", icon: BarChart3, modes: allModes },
  { name: "Level Up", href: "level-up", icon: Rocket, modes: allModes, featureFlag: 'level_up' },
];

// Combined navigable routes for AppModeContext to derive modeRoutes
export const allNavigableRoutes = [...navigationItems, ...toolsItems];
